import React from "react";
import { Box, Button } from "@showitinc/showit-wc";

import SizeAndPosition from "../app2components/panel/element/SizeAndPosition/SizeAndPosition";
import ElementPropertiesEffects from "./ElementPropertiesEffects";
import ElementPropertiesAnimation from "./ElementPropertiesAnimation";
import ElementPropertiesHover from "./ElementPropertiesHover";
import ActionProperties from "../app2components/panel/element/ActionProperties";
import AppConstants from "../constants/AppConstants";
import BlockUpdateVO from "../vo/BlockUpdate.mjs";
import BlockUtils from "../utils/BlockUtils";
import DocumentStore from "../document/DocumentStore";
import { COMMANDS } from "../document/commands";
import When from "./utils/When";
import { batchPropChanges } from "./utils/PropsUpdater";
import AdvancedProperties from "../app2components/panel/shared/advanced/AdvancedProperties";
import UIActions from "../actions/UIActions";
import PropertyPanelSectionWrapper from "../app2components/base/PropertyPanelSectionWrapper";
import ElementTypeProperties from "./ElementTypeProperties";
import { applyStyle, deviatePropFromUpdate, isDeviation } from "../utils/InheritanceUtils";
import { ensureArray } from "../utils/JSUtils";
import { getUpdateType } from "../utils/PropUpdateUtils";
import { DEVIATE, STYLE, SPLIT } from "../constants/PropUpdateConstants.mjs";
import { IS_DEVIATION } from "../constants/DesignConstants.mjs";
import { replaceSync } from "../utils/MulticastUtils";
import {
  canRemoveFromGroup,
  removeSelectionFromGroup,
  canSelectedGroupDissolve,
  dissolveSelection,
  generateCleanActionUpdates,
} from "@/utils/GroupUtils";
import DataTypes, { GROUP } from "@/constants/DataTypes";
import { checkSelectionForFormFieldConversion } from "@/utils/PropertyUtils";
import useV2Feature from "@/hooks/useV2Feature";
import { FORMS_FLAG } from "@/constants/FeatureFlags.mjs";
import { isFormFieldElementType } from "@/constants/FormFieldConstants.mjs";
import ConvertToFormFieldButton from "@/app2components/panel/element/ConvertToFormFieldButton";

/**
 * @typedef {import("../vo/BlockUpdate.mjs").PropUpdate} PropUpdate
 */

const {
  ELEMENT_TYPE_BUTTON,
  ELEMENT_TYPE_LOTTIE,
  ELEMENT_TYPE_SOCIAL_GRID,
  LOCK_EDITING_KEY,
  PANEL_SECTION_ELEMENT_SIZE,
  PANEL_SECTION_ELEMENT_ACTIONS,
  PANEL_SECTION_ELEMENT_ANIMATION,
  PANEL_SECTION_ELEMENT_HOVER,
  PANEL_SECTION_ELEMENT_EFFECTS,
  PANEL_SECTION_ELEMENT_CLASSNAME,
} = AppConstants;

const ElementProperties = ({
  block,
  elementId,
  element,
  isWP,
  layout,
  mappedElement,
  selectedElements,
  theme,
  viewState,
}) => {
  const _onPropUpdate = (/** @type {PropUpdate} */ propInfo) => {

    const update = new BlockUpdateVO();

    const updateType = getUpdateType({ elementData: element, propInfo, theme });

    const preparedUpdates =
      updateType === STYLE
        ? applyStyle(element, propInfo, layout)
        : updateType === SPLIT
        ? replaceSync(propInfo, element, SPLIT)
        : updateType === DEVIATE
        ? deviatePropFromUpdate(element, propInfo, theme)
        : ensureArray(propInfo);


    const updaterCB = (/** @type {Array<PropUpdate>} */ propUpdate) => {
      ensureArray(propUpdate).forEach((singleUpdate) =>
        update.addPropUpdate(elementId, singleUpdate)
      );
    };

    batchPropChanges(preparedUpdates, updaterCB);

    // additional updates
    if (element.get("type") === DataTypes.GROUP) {
      const hasActionUpdate = ensureArray(propInfo).some(
        ({ name, value }) => name.includes("link") || (name.includes("pubs") && !!value)
      );
      if (hasActionUpdate) {
        const cleanupUpdates = generateCleanActionUpdates(element, elementId);
        Object.entries(cleanupUpdates).forEach(([id, updates]) =>
          updates.forEach((singleUpdate) => update.addPropUpdate(id, singleUpdate))
        );
      }
    }

    update.collapseUndos();

    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });
  };

  const _togglePanelSection = (sectionName) => {
    UIActions.toggleElementPropertiesSection({ name: sectionName });
  };

  const inSiteBlock = BlockUtils.isSiteBlock(block); // is element inside a site block
  const activeSection = viewState.get("activeSection");

  // to be a WP element you need to be inside a wordpress page
  const wpField = element.getIn(["wp", "field"]);
  const isWPElement = wpField && isWP;
  const isLocked = element.get(LOCK_EDITING_KEY) === "1";
  const elementType = element.getIn(["type"]);
  const hover = element.get("hover", null);

  const preparedElement = element.set(IS_DEVIATION, isDeviation(element));
  const canRemove = canRemoveFromGroup(selectedElements);
  const canDissolve = canSelectedGroupDissolve(selectedElements);
  const isFormsEnabled = useV2Feature(FORMS_FLAG);
  const canConvertToFormField =
    isFormsEnabled && checkSelectionForFormFieldConversion(selectedElements, block);

  return (
    <>
      {canDissolve && (
        <Box pt="4" pb="3" px="4">
          <Button
            fullWidth
            iconPrefix="unGroup"
            iconSize="20"
            variant="minimal"
            onClick={() => dissolveSelection(selectedElements, block)}
          >
            Ungroup Elements
          </Button>
        </Box>
      )}
      {canRemove && (
        <Box pt="4" pb="3" px="4">
          <Button
            fullWidth
            iconPrefix="removeFromGroup"
            iconSize="20"
            variant="minimal"
            onClick={() => removeSelectionFromGroup(selectedElements, block)}
          >
            Remove from Group
          </Button>
        </Box>
      )}
      {canConvertToFormField && (
        <Box pt="4" pb="3" px="4">
          <ConvertToFormFieldButton selectedElements={selectedElements} />
        </Box>
      )}
      <ElementTypeProperties
        activeSection={activeSection}
        block={block}
        elementId={elementId}
        element={element}
        preparedElement={preparedElement}
        mappedElement={mappedElement}
        isLocked={isLocked}
        isWP={isWP}
        layout={layout}
        onPropUpdate={_onPropUpdate}
        onToggle={_togglePanelSection}
        theme={theme}
      />
      <PropertyPanelSectionWrapper
        title="Size & Position"
        data-testid="size-and-position-section"
        open={activeSection === PANEL_SECTION_ELEMENT_SIZE}
        onSIPanelToggle={() => _togglePanelSection(PANEL_SECTION_ELEMENT_SIZE)}
      >
        <SizeAndPosition
          isLocked={isLocked}
          elementId={elementId}
          element={element}
          layout={layout}
          theme={theme}
          onPropUpdate={_onPropUpdate}
        />
      </PropertyPanelSectionWrapper>
      <When
        it={
          elementType !== ELEMENT_TYPE_SOCIAL_GRID &&
          !isFormFieldElementType(elementType)
        }
      >
        <PropertyPanelSectionWrapper
          title="Click Actions"
          helpTooltip="click-actions"
          open={activeSection === PANEL_SECTION_ELEMENT_ACTIONS}
          onSIPanelToggle={() => _togglePanelSection(PANEL_SECTION_ELEMENT_ACTIONS)}
        >
          <ActionProperties
            block={block}
            element={element}
            isLocked={isLocked}
            isWP={isWP}
            isWPElement={isWPElement}
            inSiteBlock={inSiteBlock}
            onPropUpdate={_onPropUpdate}
          />
        </PropertyPanelSectionWrapper>
      </When>
      <PropertyPanelSectionWrapper
        title="Animation"
        open={activeSection === PANEL_SECTION_ELEMENT_ANIMATION}
        onSIPanelToggle={() => _togglePanelSection(PANEL_SECTION_ELEMENT_ANIMATION)}
      >
        <ElementPropertiesAnimation
          element={element}
          isLocked={isLocked}
          layout={layout}
          onPropUpdate={_onPropUpdate}
        />
      </PropertyPanelSectionWrapper>
      <When
        it={
          elementType !== ELEMENT_TYPE_SOCIAL_GRID &&
          elementType !== ELEMENT_TYPE_BUTTON &&
          !isFormFieldElementType(elementType)
        }
      >
        {elementType !== GROUP && (
          <PropertyPanelSectionWrapper
            title="Hover"
            open={activeSection === PANEL_SECTION_ELEMENT_HOVER}
            onSIPanelToggle={() => _togglePanelSection(PANEL_SECTION_ELEMENT_HOVER)}
          >
            <ElementPropertiesHover
              hover={hover}
              isLocked={isLocked}
              elementType={elementType}
              onPropUpdate={_onPropUpdate}
              hideColor={element.getIn(["desktop", "fillType"]) === "gradient"}
            />
          </PropertyPanelSectionWrapper>
        )}
        <When it={elementType !== ELEMENT_TYPE_LOTTIE}>
          <PropertyPanelSectionWrapper
            title="Effects"
            open={activeSection === PANEL_SECTION_ELEMENT_EFFECTS}
            onSIPanelToggle={() => _togglePanelSection(PANEL_SECTION_ELEMENT_EFFECTS)}
          >
            <ElementPropertiesEffects
              isLocked={isLocked}
              element={element}
              layout={layout}
              onPropUpdate={_onPropUpdate}
              preparedElement={element}
            />
          </PropertyPanelSectionWrapper>
        </When>
      </When>
      <PropertyPanelSectionWrapper
        title="Advanced"
        open={activeSection === PANEL_SECTION_ELEMENT_CLASSNAME}
        onSIPanelToggle={() => _togglePanelSection(PANEL_SECTION_ELEMENT_CLASSNAME)}
      >
        <AdvancedProperties entity={element} isLocked={isLocked} />
      </PropertyPanelSectionWrapper>
    </>
  );
};

export default ElementProperties;
